import { z } from "zod";
import {
  LIFE_STAGE_AGES,
  LIFE_STAGES,
  type LifeStage,
  QUESTION_EXPECTS,
  QUESTIONS,
  type Question,
} from "./questions";

/**
 * Le tirage des questions de Ma timeline (spec, section 3.6 ; ticket 5, #512). Fonctions pures,
 * testees dans `test/app-question-draw.test.ts` ; le backend les appelle avec ce que la base a.
 *
 * **Les trous.** Trois mesures, de la plus forte a la plus faible :
 * 1. les periodes de vie (`enfance` 0-10 ans ... `adulte` 25 ans et plus) : nombre de points dont
 *    le debut (`startFrom`) tombe dans la periode, d'apres la date de naissance ;
 * 2. les pistes : nombre de points de la piste visee ;
 * 3. les decennies creuses (annees 1990, 2000...) entre la naissance et aujourd'hui, sans aucun
 *    point : elles departagent deux questions dont la periode a autant de points.
 *
 * **Le tirage.** Une question deja repondue ou passee ne revient jamais ; une question d'une
 * periode pas encore vecue (« ta vie d'adulte » a 20 ans) n'est pas proposee. Parmi les autres :
 * - celles mises « plus tard » passent apres toutes les autres, la plus anciennement repoussee
 *   d'abord (la fin de file) ;
 * - sinon le plus gros trou d'abord : le nombre de points de sa periode quand la question en a une
 *   et que la date de naissance est connue, sinon le nombre de points de sa piste ; une question de
 *   concert (point « a dater ») compte zero ;
 * - puis la decennie creuse, puis la piste la moins remplie ;
 * - a egalite, un ordre melange par une graine du jour (`seedDay`, en general la date du jour) :
 *   stable dans une journee, different le lendemain.
 *
 * **Sans date de naissance**, l'age d'un point ne se calcule pas : ni periodes de vie, ni
 * decennies. Le tirage se rabat sur les pistes, et `stageGaps` rend une liste vide (le frontal
 * invite alors a renseigner la date de naissance).
 *
 * **Les concerts.** Chaque point « a dater » de la piste musique dont le titre commence par
 * « Concert : » donne une question `concert-<id du point>` ; y repondre date ce point au lieu d'en
 * creer un autre.
 */

// ---------------------------------------------------------------------------------------------
// Les formes manipulees
// ---------------------------------------------------------------------------------------------

export const QUESTION_STATUSES = ["answered", "skipped", "later"] as const;
export type QuestionStatus = (typeof QUESTION_STATUSES)[number];

/** Une ligne de `question_state`. */
export type QuestionState = {
  questionId: string;
  status: QuestionStatus;
  /** ISO 8601 : l'ordre des questions mises « plus tard ». */
  updatedAt: string;
};

/** Ce que le tirage lit d'un point. */
export type DrawEntry = {
  id: number;
  trackId: number;
  title: string;
  startFrom: string | null;
  undated: boolean;
};

/** Ce que le tirage lit d'une piste. */
export type DrawTrack = { id: number; slug: string; name: string };

export type StageGap = { stage: LifeStage; count: number };

/** Pourquoi une question a ete tiree. */
export type DrawReason =
  | { kind: "stage"; stage: LifeStage; count: number }
  | { kind: "track"; trackName: string; count: number }
  | { kind: "undated" }
  | { kind: "later" };

export type Draw = { question: Question; reason: DrawReason };

// ---------------------------------------------------------------------------------------------
// Les ages et les periodes
// ---------------------------------------------------------------------------------------------

const parts = (iso: string): [number, number, number] => {
  const [y, m, d] = iso.split("-").map(Number);
  return [y ?? 0, m ?? 0, d ?? 0];
};

/** L'age revolu a une date (`AAAA-MM-JJ`), en comparant les chaines : pas de fuseau. */
export function ageAt(birthDate: string, date: string): number {
  const [by, bm, bd] = parts(birthDate);
  const [y, m, d] = parts(date);
  const beforeBirthday = m < bm || (m === bm && d < bd);
  return y - by - (beforeBirthday ? 1 : 0);
}

/** La periode de vie d'un age ; `null` avant la naissance. */
export function stageOfAge(age: number): LifeStage | null {
  if (age < 0) return null;
  for (const stage of LIFE_STAGES) {
    const { max } = LIFE_STAGE_AGES[stage];
    if (max === null || age <= max) return stage;
  }
  return null;
}

/** Les periodes deja commencees a `today`. */
export function livedStages(birthDate: string, today: string): LifeStage[] {
  const age = ageAt(birthDate, today);
  return LIFE_STAGES.filter((stage) => LIFE_STAGE_AGES[stage].min <= age);
}

/**
 * Le nombre de points par periode deja commencee, dans l'ordre de la vie (la jauge « Ou ta frise
 * est encore vide »). Liste vide sans date de naissance. Un point a dater ne compte nulle part.
 */
export function stageGaps(
  entries: readonly DrawEntry[],
  birthDate: string | null,
  today: string,
): StageGap[] {
  if (birthDate === null) return [];
  const counts = new Map<LifeStage, number>();
  for (const entry of entries) {
    if (entry.startFrom === null) continue;
    const stage = stageOfAge(ageAt(birthDate, entry.startFrom));
    if (stage !== null) counts.set(stage, (counts.get(stage) ?? 0) + 1);
  }
  return livedStages(birthDate, today).map((stage) => ({ stage, count: counts.get(stage) ?? 0 }));
}

/** Les decennies (1990, 2000...) entre la naissance et aujourd'hui sans aucun point. */
export function emptyDecades(
  entries: readonly DrawEntry[],
  birthDate: string,
  today: string,
): number[] {
  const decade = (iso: string) => Math.floor(parts(iso)[0] / 10) * 10;
  const filled = new Set(
    entries.flatMap((entry) => (entry.startFrom === null ? [] : [decade(entry.startFrom)])),
  );
  const empty: number[] = [];
  for (let d = decade(birthDate); d <= decade(today); d += 10) {
    if (!filled.has(d)) empty.push(d);
  }
  return empty;
}

/** Vrai quand les annees d'une periode (bornees a aujourd'hui) touchent une decennie creuse. */
function stageTouchesEmptyDecade(
  stage: LifeStage,
  birthDate: string,
  today: string,
  empty: readonly number[],
): boolean {
  const birthYear = parts(birthDate)[0];
  const { min, max } = LIFE_STAGE_AGES[stage];
  const first = birthYear + min;
  const last = Math.min(
    parts(today)[0],
    max === null ? Number.POSITIVE_INFINITY : birthYear + max + 1,
  );
  return empty.some((d) => d <= last && d + 9 >= first);
}

// ---------------------------------------------------------------------------------------------
// Les questions des concerts
// ---------------------------------------------------------------------------------------------

export const CONCERT_PREFIX = "Concert : ";
export const CONCERT_QUESTION_PREFIX = "concert-";

/** Une question par point « a dater » de la piste musique titre « Concert : {groupe} ». */
export function concertQuestions(
  entries: readonly DrawEntry[],
  tracks: readonly DrawTrack[],
): Question[] {
  const music = tracks.find((track) => track.slug === "musique");
  if (music === undefined) return [];
  return entries
    .filter(
      (entry) =>
        entry.undated &&
        entry.trackId === music.id &&
        entry.title.startsWith(CONCERT_PREFIX) &&
        entry.title.length > CONCERT_PREFIX.length,
    )
    .map((entry) => ({
      id: `${CONCERT_QUESTION_PREFIX}${String(entry.id)}`,
      text: `Ton premier concert de ${entry.title.slice(CONCERT_PREFIX.length).trim()}, c'était vers quel âge ?`,
      track: "musique",
      expects: "point",
      entryId: entry.id,
    }));
}

/** Toutes les questions du moment : la banque, puis celles des concerts. */
export function allQuestions(
  entries: readonly DrawEntry[],
  tracks: readonly DrawTrack[],
  bank: readonly Question[] = QUESTIONS,
): Question[] {
  return [...bank, ...concertQuestions(entries, tracks)];
}

// ---------------------------------------------------------------------------------------------
// Le tirage
// ---------------------------------------------------------------------------------------------

/** FNV-1a sur 32 bits : un melange deterministe, sans dependance. */
export function seededRank(seed: string, id: string): number {
  let hash = 0x811c9dc5;
  for (const char of `${seed}:${id}`) {
    hash ^= char.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

export type DrawInput = {
  questions: readonly Question[];
  states: readonly QuestionState[];
  entries: readonly DrawEntry[];
  tracks: readonly DrawTrack[];
  birthDate: string | null;
  today: string;
  /** La graine du melange des ex aequo ; par defaut `today`. */
  seedDay?: string;
};

type Candidate = {
  question: Question;
  later: string | null;
  gap: number;
  decade: 0 | 1;
  trackCount: number;
  rank: number;
  reason: DrawReason;
};

/** La question suivante, ou `null` quand il n'en reste aucune. */
export function nextQuestion(input: DrawInput): Draw | null {
  const ordered = rankQuestions(input);
  return ordered[0] ?? null;
}

/** Toutes les questions encore ouvertes, dans l'ordre ou elles seront tirees. */
export function rankQuestions(input: DrawInput): Draw[] {
  const { questions, states, entries, tracks, birthDate, today } = input;
  const seed = input.seedDay ?? today;
  const stateOf = new Map(states.map((state) => [state.questionId, state]));
  const trackBySlug = new Map(tracks.map((track) => [track.slug, track]));
  const perTrack = new Map<number, number>();
  for (const entry of entries) perTrack.set(entry.trackId, (perTrack.get(entry.trackId) ?? 0) + 1);
  const stages = new Map(stageGaps(entries, birthDate, today).map((g) => [g.stage, g.count]));
  const empty = birthDate === null ? [] : emptyDecades(entries, birthDate, today);

  const candidates: Candidate[] = [];
  for (const question of questions) {
    const state = stateOf.get(question.id);
    if (state !== undefined && state.status !== "later") continue;
    const track = trackBySlug.get(question.track);
    const trackCount = track === undefined ? 0 : (perTrack.get(track.id) ?? 0);
    let gap: number;
    let reason: DrawReason;
    let decade: 0 | 1 = 1;
    if (question.entryId !== undefined) {
      gap = 0;
      reason = { kind: "undated" };
    } else if (birthDate !== null && question.lifeStage !== undefined) {
      const count = stages.get(question.lifeStage);
      // Une periode pas encore vecue : la question attendra.
      if (count === undefined) continue;
      gap = count;
      reason = { kind: "stage", stage: question.lifeStage, count };
      if (stageTouchesEmptyDecade(question.lifeStage, birthDate, today, empty)) decade = 0;
    } else {
      gap = trackCount;
      reason = { kind: "track", trackName: track?.name ?? question.track, count: trackCount };
    }
    const later = state?.status === "later" ? state.updatedAt : null;
    candidates.push({
      question,
      later,
      gap,
      decade,
      trackCount,
      rank: seededRank(seed, question.id),
      reason: later === null ? reason : { kind: "later" },
    });
  }

  candidates.sort((a, b) => {
    if ((a.later === null) !== (b.later === null)) return a.later === null ? -1 : 1;
    if (a.later !== null && b.later !== null && a.later !== b.later) {
      return a.later < b.later ? -1 : 1;
    }
    return (
      a.gap - b.gap ||
      a.decade - b.decade ||
      a.trackCount - b.trackCount ||
      a.rank - b.rank ||
      (a.question.id < b.question.id ? -1 : 1)
    );
  });
  return candidates.map(({ question, reason }) => ({ question, reason }));
}

// ---------------------------------------------------------------------------------------------
// La raison, en francais
// ---------------------------------------------------------------------------------------------

const STAGE_SUBJECTS: Record<LifeStage, { subject: string; plural: boolean }> = {
  enfance: { subject: "ton enfance", plural: false },
  college: { subject: "tes années de collège", plural: true },
  lycee: { subject: "tes années de lycée", plural: true },
  etudes: { subject: "tes années de 18 à 24 ans", plural: true },
  adulte: { subject: "ta vie d'adulte", plural: false },
};

const howMany = (count: number, plural: boolean): string => {
  const verb = plural ? "n'ont" : "n'a";
  if (count === 0) return `${verb} encore aucun point`;
  if (count === 1) return `${verb} encore qu'un point`;
  return `${verb} encore que ${String(count)} points`;
};

/** La ligne « Pourquoi celle-ci : ... », sans son debut. */
export function reasonText(reason: DrawReason): string {
  switch (reason.kind) {
    case "stage": {
      const { subject, plural } = STAGE_SUBJECTS[reason.stage];
      return `${subject} ${howMany(reason.count, plural)}.`;
    }
    case "track":
      return `ta piste ${reason.trackName} ${howMany(reason.count, false)}.`;
    case "undated":
      return "ce concert attend encore sa date.";
    case "later":
      return "tu l'avais gardée pour plus tard.";
  }
}

// ---------------------------------------------------------------------------------------------
// Le contrat des routes (`/api/t/questions/*`)
// ---------------------------------------------------------------------------------------------

/** Un identifiant de question : la contrainte de `question_state.question_id`. */
export const QUESTION_ID = /^[a-z0-9-]{1,64}$/;

export const lifeStageSchema = z.enum(LIFE_STAGES);

export const drawnQuestionSchema = z.object({
  id: z.string(),
  text: z.string(),
  track: z.string(),
  lifeStage: lifeStageSchema.nullable(),
  expects: z.enum(QUESTION_EXPECTS),
  /** Le point « a dater » qu'une reponse date (question de concert), sinon `null`. */
  entryId: z.number().int().positive().nullable(),
  /** « tes années de collège n'ont encore que 2 points. » */
  reason: z.string(),
});
export type DrawnQuestion = z.infer<typeof drawnQuestionSchema>;

export const nextQuestionSchema = z.object({
  question: drawnQuestionSchema.nullable(),
  answered: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  gaps: z.array(z.object({ stage: lifeStageSchema, count: z.number().int().nonnegative() })),
});
export type NextQuestion = z.infer<typeof nextQuestionSchema>;

/** Une question tiree, telle que la route la rend. */
export function drawnQuestion(draw: Draw): DrawnQuestion {
  const { question, reason } = draw;
  return {
    id: question.id,
    text: question.text,
    track: question.track,
    lifeStage: question.lifeStage ?? null,
    expects: question.expects,
    entryId: question.entryId ?? null,
    reason: reasonText(reason),
  };
}

/**
 * Les compteurs « N repondues · M en tout » : la banque, les concerts du moment, et les concerts
 * deja repondus (dates depuis, ils ne sont plus fabriques mais restent comptes).
 */
export function questionCounts(
  questions: readonly Question[],
  states: readonly QuestionState[],
): { answered: number; total: number } {
  const known = new Set(questions.map((question) => question.id));
  for (const state of states) {
    if (state.status === "answered" && state.questionId.startsWith(CONCERT_QUESTION_PREFIX)) {
      known.add(state.questionId);
    }
  }
  const answered = states.filter(
    (state) => state.status === "answered" && known.has(state.questionId),
  ).length;
  return { answered, total: known.size };
}
